import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import {
  Plus, Clock, PieChart, BookOpen, ArrowRight, AlertCircle,
  Info, ChevronRight, GraduationCap, Layers, FilePlus
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KpiCard = ({ icon: Icon, label, children }) => (
  <div className="flex-1 flex items-center gap-3 px-6 py-5" data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <div className="w-10 h-10 rounded-full bg-white/60 flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5 text-violet-500" />
    </div>
    <div>
      <span className="text-sm text-slate-600 block">{label}</span>
      <div className="text-xl font-black text-violet-600 mt-0.5">{children}</div>
    </div>
  </div>
);

const SessionRow = ({ session, onClick }) => (
  <div
    className="flex items-center gap-6 bg-white border border-slate-200 rounded-xl px-6 py-4 hover:border-violet-200 transition-colors cursor-pointer"
    onClick={onClick}
    data-testid={`session-row-${session.session_id}`}
  >
    <div className="flex-1 min-w-0">
      <h4 className="text-sm font-bold text-slate-900 truncate">{session.title}</h4>
      {(session.start_date || session.end_date) && (
        <p className="text-xs text-slate-400 mt-0.5">
          Du {session.start_date || '—'} au {session.end_date || '—'}
        </p>
      )}
    </div>
    <div className="w-72 shrink-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs text-slate-400">Avancement global</span>
        <span className="text-xs font-bold text-violet-600">{session.progress.toFixed(2)}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full bg-violet-500 rounded-full transition-all duration-700"
          style={{ width: `${Math.min(session.progress, 100)}%` }} />
      </div>
    </div>
    <div className="flex items-center gap-2 shrink-0 w-32 justify-end">
      {session.corrections > 0 ? (
        <>
          <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center">
            <AlertCircle className="h-4 w-4 text-amber-500" />
          </div>
          <span className="text-xs font-medium text-amber-600">{session.corrections} correction{session.corrections > 1 ? 's' : ''}</span>
        </>
      ) : (
        <>
          <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center">
            <Info className="h-4 w-4 text-violet-400" />
          </div>
          <span className="text-xs text-slate-400">0 correction</span>
        </>
      )}
    </div>
  </div>
);

const ElearningDashboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/elearning/dashboard`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setData(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalHours = data?.total_hours ?? 0;
  const totalMins = data?.total_mins ?? 0;
  const totalLearners = data?.total_learners ?? 0;
  const avgPerLearner = data?.avg_per_learner ?? 0;
  const avgRate = data?.avg_rate ?? 0;
  const sessions = data?.sessions ?? [];

  return (
    <Layout>
      <div className="space-y-8" data-testid="elearning-dashboard">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="breadcrumb">
          <span>LMS</span>
          <ChevronRight className="h-3.5 w-3.5" />
          <span className="text-slate-600 font-medium">Tableau de bord LMS</span>
        </div>

        <h1 className="text-3xl font-black text-slate-900" data-testid="dashboard-title">Tableau de bord LMS</h1>

        {/* KPI Cards - lavender band */}
        <div>
          <h2 className="text-lg font-bold text-slate-900 mb-4">Ces 30 derniers jours</h2>
          <div className="bg-violet-50/70 rounded-2xl flex divide-x divide-violet-100" data-testid="kpi-cards">
            <KpiCard icon={Clock} label="Total e-learning">
              {loading ? '—' : `${totalHours}h et ${totalMins} min`}
            </KpiCard>
            <KpiCard icon={GraduationCap} label="Moyenne e-learning par apprenant">
              {loading ? '—' : <>{avgPerLearner} min <span className="text-sm font-medium text-slate-400">({totalLearners} apprenants)</span></>}
            </KpiCard>
            <KpiCard icon={PieChart} label="Taux moyen de e-learning par formation">
              {loading ? '—' : `${avgRate} %`}
            </KpiCard>
          </div>
        </div>

        {/* 3 Action cards */}
        <div className="grid grid-cols-3 gap-5" data-testid="action-cards">
          {/* Créer une séquence vierge - Orange */}
          <div className="relative rounded-2xl overflow-hidden h-52 cursor-pointer group"
            onClick={() => navigate('/elearning/sequences')}
            data-testid="card-create-sequence">
            <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500">
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 300 220">
                <circle cx="150" cy="110" r="120" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="35" />
                <circle cx="180" cy="130" r="80" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="25" />
              </svg>
            </div>
            <div className="relative h-full flex flex-col justify-between p-5">
              <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <FilePlus className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-3">Créer une séquence vierge</h3>
                <button className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                  data-testid="btn-create-sequence">
                  <Plus className="h-4 w-4" />Créer
                </button>
              </div>
            </div>
          </div>

          {/* Mes séquences - Green */}
          <div className="relative rounded-2xl overflow-hidden h-52 cursor-pointer group"
            onClick={() => navigate('/elearning/sequences')}
            data-testid="card-sequences">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 to-teal-600">
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 300 220">
                <circle cx="140" cy="110" r="120" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="35" />
                <circle cx="170" cy="130" r="80" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="25" />
              </svg>
            </div>
            <div className="relative h-full flex flex-col justify-between p-5">
              <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <BookOpen className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-3">Mes séquences</h3>
                <button className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                  data-testid="btn-view-sequences">
                  <ArrowRight className="h-4 w-4" />Voir
                </button>
              </div>
            </div>
          </div>

          {/* Mes parcours - Coral */}
          <div className="relative rounded-2xl overflow-hidden h-52 cursor-pointer group"
            onClick={() => navigate('/elearning/parcours')}
            data-testid="card-parcours">
            <div className="absolute inset-0 bg-gradient-to-br from-red-400 to-rose-500">
              <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 300 220">
                <circle cx="160" cy="110" r="110" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="35" />
                <circle cx="190" cy="120" r="70" fill="none" stroke="rgba(255,255,255,0.25)" strokeWidth="25" />
              </svg>
            </div>
            <div className="relative h-full flex flex-col justify-between p-5">
              <div className="w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Layers className="h-7 w-7 text-white" />
              </div>
              <div>
                <h3 className="text-base font-bold text-white mb-3">Mes parcours</h3>
                <button className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                  data-testid="btn-view-parcours">
                  <ArrowRight className="h-4 w-4" />Voir
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sessions en cours */}
        <div data-testid="sessions-en-cours">
          <h2 className="text-lg font-bold text-slate-900 mb-5">Sessions en cours</h2>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-20 bg-slate-100 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : sessions.length === 0 ? (
            <div className="border border-slate-200 rounded-xl p-8 text-center">
              <GraduationCap className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">Aucune session e-learning en cours.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sessions.map(session => (
                <SessionRow
                  key={session.session_id}
                  session={session}
                  onClick={() => navigate(`/sessions/${session.session_id}/edit`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ElearningDashboard;
